import { CloseIcon, EditIcon, SearchIcon, ViewIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup, InputRightElement,
  Link,
  Text,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  name: string;
  city_name: string;
  actionHeader: undefined;
};

export default function AreaManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const {hasPermission} = useAuthentication()
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const { getAreas, deleteArea } = useApi();
  const { data: areas, mutate } = useCache("areas", () => getAreas(1, "", searchBarValue34));
  const handleSearch = (event: any) => {
    // S'assurer que la valeur est toujours une chaîne, même si undefined ou null
    const value = event.target?.value ?? "";
    setSearchBarValue34(value);
    // Déclencher la recherche avec le nouveau keyword
    mutate(getAreas(1, "", value));
  };
  const handleClearSearch = () => {
    setSearchBarValue34("");
    mutate(getAreas(1, "", ""));
  };
  const handleViewItem = useCallback((item: any) => {
    setActiveItem(item);
    onOpen();
  },[onOpen,setActiveItem])
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Commune</Heading>
          </Flex>
        ),
        meta: { width: "190px" },
      }),
      table3ColumnHelper.accessor("city_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="16px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Ville</Heading>
          </Flex>
        ),
        meta: { width: "190px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            <IconButton
              variant="outline"
              colorScheme="gray_stroke_0"
              aria-label="Edit"
              size={"sm"}
              icon={<EditIcon />}
              borderRadius={"4px"}
              onClick={() => navigate(`edit/${info.getValue()}`)}
            />
            {hasPermission("area", "get") && <IconButton
              variant="outline"
              colorScheme="gray_stroke_0"
              aria-label="view"
              size={"sm"}
              icon={<ViewIcon />}
              borderRadius={"4px"}
              onClick={() => handleViewItem(info.row.original)}
            />}
            {(hasPermission("area", "delete") || hasPermission("area", "delete")) && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex px="15px" py="14px" justifyContent={"flex-end"}>
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [navigate, handleViewItem, hasPermission, isDeleting, activeItem, onConfirmOpen]);

  return (
    <>
      {/* dealer management section */}
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minH={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Commune
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              

              {hasPermission("area", "create") && <Button
                size="xl"
                variant="outline"
                colorScheme="red_origin"
                leftIcon={
                  <Image
                    src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                    alt="Handshake-deal Svgrepo.com"
                  />
                }
                fontWeight={700}
                gap="8px"
                minW="128px"
                borderRadius="8px"
                as={Link}
                href="/areas/add"
              >
                Créer une commune
              </Button>}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                
                <Input
                  placeholder={`Rechercher`}
                  value={searchBarValue34 || ""}
                  onChange={handleSearch}
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
                <IconButton onClick={() => mutate(getAreas(1, "", searchBarValue34 || ""))} ml={2} borderRadius={"10px"} py={"19px"} border={"1px solid #e2e4e6"} aria-label="search" icon={<SearchIcon/>} justifyContent={"center"} alignItems={"center"} />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={areas}
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche Commune</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            <HStack>
              {hasPermission("area", "update") && <Button
                size="xl"
                variant="outline"
                colorScheme="green_360"
                leftIcon={<EditIcon />}
                fontWeight={700}
                gap="8px"
                borderRadius="8px"
                as={Link}
                href={`/areas/edit/${activeItem?.id}`}
              >
                Modifier
              </Button>}
              {(hasPermission("area", "delete") || hasPermission("area", "delete")) && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={<DeleteIcon />}
                  fontWeight={700}
                  gap="8px"
                  borderRadius="8px"
                  onClick={() => {
                    onConfirmOpen();
                  }}
                  isDisabled={isDeleting}
                  isLoading={isDeleting}
                >
                  Supprimer
                </Button>
              )}
            </HStack>
            <AppViewListItem label="Nom" value={activeItem?.name} />
            {/* <AppViewListItem
              label="Nom du dealer"
              value={activeItem?.dealer_name}
            /> */}
            <AppViewListItem label="Ville" value={activeItem?.city_name} />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer la commune « {activeItem?.name} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteArea(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Commune supprimée avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(getAreas(1, "", searchBarValue34 || ""));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
